import React, { useEffect, useState, useCallback } from 'react';

import { isEmpty } from 'lodash';
import { Select, Row, Col, Label, FormGroup, Input, InputGroup, InputGroupText, SimpleAlert, Badge, Switch, Button } from 'twyne-new.ui';

import ContinueNoConsent from '@Components/ContinueNoConsent';
import { TcpaConsentDbaProvider } from '@Components/tcpaConsentDba/Context';
import TcpaConsentDbaModal from '@Components/tcpaConsentDba/Modal';
import TcpaConsentDbaTriggerButton from '@Components/tcpaConsentDba/TriggerButton';
import { useGlobalStore } from '@Stores/global';
import Checkbox from 'components/Checkbox';
import InfoTip from 'components/InfoTip';
import TemplateGroup from 'components/TemplateGroup';
import CodeEditor from 'components/form/CodeEditor';
import InputForm from 'components/form/Input';
import { PureSwitch } from 'components/form/Switch';
import TwyneURL from 'components/formsy/TwyneURL';
import { DropdownMergeTag } from 'modules/settings/views/integration/components/SweepContestFinishedForm/utils';
import { getDateFormats } from 'utils';

import { useFormStore } from '../../store';
import * as fieldsUtil from '../fieldsUtil'
import { ChevronDown } from 'lucide-react';

const isFieldType = (...field_types) => col => field_types.includes(col.field_type) || field_types.includes(col.field)
const isDatasource = (trueCase, falseCase) => col => col.field_datasource ? trueCase && trueCase(col) : falseCase && falseCase(col)
const isAll = (...checkers) => col => checkers.every(checker => checker(col))

const COL_SIZES = [{ value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }, { value: 6, label: '6' }]

export default class PropertiesComponent extends React.Component {
  get colsSizeOpts() {
    const { cols, col = {} } = this.props
    const min = 3

    const sized = cols.reduce((total, { size }) => total + (size || min), 0) - (col.size || min)

    const base = [{ value: '', label: 'auto' }]
    const remains = 12 - min - sized

    if (remains < 1 && col.size) {
      return base.concat({ label: col.size, value: col.size, disabled: true })
    }

    if (remains === 0) {
      return base.concat({ label: min, value: min })
    }

    if (remains >= 1) {
      return base
        .concat({ label: min, value: min })
        .concat(Array(remains || 1)
          .fill(null)
          .map((_, i) => ({
            value: i + 1 + min,
            label: i + 1 + min,
          })))
    }

    return base
  }

  get hasAutoSubmitBehavior() {
    const { col } = this.props

    if (col.field_component?.startsWith('buttons_grid') && !col.multipleAnswers) {
      return true
    }

    return ['state', 'gender', 'title'].includes(col.field) || col.custom_field && ['select', 'checkbox', 'radio', 'boolean', 'checkbox_unique'].includes(col.field_component)
  }

  getComponents = col => this.components.filter(({ on }) => on(col))

  components = [
    { value: "select", label: "Dropdown (unique)", on: isDatasource(isFieldType('input'), isFieldType('gender', 'select')) },
    { value: "radio", label: "Radio (unique)", on: isDatasource(isFieldType('input'), isFieldType('gender', 'select')) },
    { value: "checkbox", label: "Checkbox (multiple)", on: isDatasource(isFieldType('input'), isFieldType('select')) },

    { value: "checkbox", label: "Checkbox + Text", on: isFieldType('tcpa_consent', 'registration_consent', 'recording_consent', 'bundled_consent') },
    { value: "checkbox_instructions_text", label: "Checkbox + Instructions + Text", on: isFieldType('tcpa_consent') },

    { value: "text", label: "Only Text", on: isDatasource(null, isFieldType('tcpa_consent', 'registration_consent', 'input', 'recording_consent', 'bundled_consent')) },
    { value: "use_reg_consent", label: "Use registration consent", on: isAll(isFieldType('recording_consent'), () => this.props.selectedFields?.registration_consent) },
    { value: "password", label: "Password", on: isDatasource(null, isFieldType('input')) },

    { value: "datepicker", label: "Datepicker", on: isDatasource(null, isFieldType('input', 'date')) },
    { value: "date_triple_select", label: "Select", on: isDatasource(null, isFieldType('date_triple_select', 'date')) }, // date_triple_select
    { value: "zipcode", label: "ZIP Code", on: isDatasource(null, isFieldType('input')) },
    { value: "statelist", label: "State List", on: isDatasource(null, isFieldType('input')) },
    { value: "textarea", label: "Textarea", on: isDatasource(null, isFieldType('input')) },

    { value: "number", label: "Number", on: isFieldType('integer') },
    { value: "checkbox_unique", label: "Checkbox (unique)", on: isFieldType('boolean') },
    { value: "boolean", label: "Radio (unique)", on: isFieldType('boolean') },
    { value: "buttons_grid", label: "Buttons", on: isFieldType('select') },
    { value: "buttons_grid_boolean", label: "Buttons", on: isFieldType('boolean') },

    { value: "h1", label: "H1", on: isFieldType('heading') },
    { value: "h2", label: "H2", on: isFieldType('heading') },
    { value: "h3", label: "H3", on: isFieldType('heading') },
    { value: "h4", label: "H4", on: isFieldType('heading') },
    { value: "h5", label: "H5", on: isFieldType('heading') },
    { value: "h6", label: "H6", on: isFieldType('heading') },

  ]

  hasExtraProperties = col => {
    const hasJumper = col.field === 'jumper'
    const hasTwyneOverride = col.field === 'twyne_registration' || col.twy_override_config
    const hasComponent = col.custom_field || ['gender', 'title', 'tcpa_consent', 'registration_consent', 'recording_consent', 'bundled_consent'].includes(col.field)
    const hasDefaultValue = ['checkbox', 'checkbox_instructions_text'].includes(col.field_component) && ['tcpa_consent', 'registration_consent', 'recording_consent', 'bundled_consent'].includes(col.field)
    const hasDefaultYear = col.field === 'dob'
    const hasHtml = col.field === 'html'
    const hasDefaultOptionComponent = col.field_component === 'select' || col.field === 'dob' || col.field_component === 'statelist'
    const hasTimerSubmit = col.field === 'timer_submit'
    const hasButtonsComponent = col.field_component === 'buttons_grid'
    const hasHeading = col.field === 'heading'

    return hasDefaultOptionComponent || hasJumper || hasTwyneOverride || hasComponent || hasDefaultValue || hasDefaultYear || hasHtml || this.hasAutoSubmitBehavior || hasTimerSubmit || hasButtonsComponent || hasHeading
  }

  getSize = col => {
    if (col.size) {
      return col.size
    }

    const sized = this.props.cols.reduce((total, { size }) => total + (size || 1), 0)
    const remains = 12 - sized
    const nonSized = this.props.cols.filter(({ size }) => !size)
    const size = Math.floor(remains / nonSized.length)
    return size
  }

  handlePropChange = col => field => value => {
    col[field] = value
    this.props.onChange(col)
  }

  TimerSubmit = ({ onChangeField }) => {
    const { col } = this.props

    if (col.field !== 'timer_submit') return null

    col.timerSubmit = col.timerSubmit || 5

    return <Col size='4'>
      <FormGroup>
        <Label>
          Submit Step after
          <small>(seconds)</small>
        </Label>
        <Input type='number' min={1} value={col.timerSubmit} onChange={e => onChangeField("timerSubmit").call(this, e.target.value)} required />
      </FormGroup>
    </Col>
  }

  TwyneTriggerType = ({ onChangeField }) => {
    const { col } = this.props

    if (col.twyne_reg_trigger_type === "percent") {
      return <Col size='12'>
        <FormGroup>
          <Label>Percent</Label>
          <Input type='number' value={col.twyne_reg_trigger_percent || '0'} onChange={e => onChangeField("twyne_reg_trigger_percent").call(this, e.target.value)} />
        </FormGroup>
      </Col>
    }

    if (col.twyne_reg_trigger_type === "fields") {
      return <Col size='12'>
        <FormGroup>
          <Label>Fields</Label>
          <Select name="twyne_reg_trigger_fields" value={col.twyne_reg_trigger_fields} required multi
            options={fieldsUtil.twyneRegistrationTriggerFields}
            onChange={e => onChangeField("twyne_reg_trigger_fields").call(this, e)} />
        </FormGroup>
      </Col>
    }

    return null
  }

  TwyneOverrideToggle = ({ onChange }) => {
    const { col } = this.props

    if (['twyne_registration'].includes(col.field)) {
      this.hasExtraProperties = this.hasExtraProperties || true
      return <Col size='12'>
        <FormGroup>
          <Label>Override configuration</Label>
          <Switch
            value={col.twy_override_config || false}
            onChange={e => onChange(e.target.checked)}
          />
        </FormGroup>
      </Col>
    }
    return null
  }

  Consent = ({ onChange, onChangeDba }) => {
    const { col } = this.props
    const [consentTexts, setConsentTexts] = useState(null)
    const { siteSettings: { useSystemConfig } } = useGlobalStore()

    const registration_consent = useSystemConfig('midpath.registration-consent-text')
    const recording_consent = useSystemConfig('midpath.recording-consent-text')
    const tcpa_consent = useSystemConfig('midpath.tcpa-consent-text')
    const tcpa_consent_dba = useSystemConfig('midpath.tcpa-consent-dba')
    const bundled_consent = useSystemConfig('midpath.bundled-consent-text')

    useEffect(() => {
      setConsentTexts({
        registration_consent: registration_consent || '',
        recording_consent: recording_consent || '',
        tcpa_consent: tcpa_consent || '',
        tcpa_consent_dba: tcpa_consent || '',
        bundled_consent: bundled_consent || '',
      })
    }, [registration_consent, recording_consent, tcpa_consent, bundled_consent])

    if (!consentTexts) {
      return null
    }

    if (['tcpa_consent', 'registration_consent', 'recording_consent', 'bundled_consent'].includes(col.field) && col.field_component !== 'use_reg_consent') {
      this.hasExtraProperties = this.hasExtraProperties || true

      return <>
        <this.SaveConsentText onChange={onChange} />
        <this.ConsentTextOverrideToggle onChange={onChange} onChangeDba={onChangeDba} defaultConsentDba={tcpa_consent_dba} defaultConsentText={consentTexts[this.props.col.field]} />
        <this.ConsentText onChange={onChange} defaultConsentDba={tcpa_consent_dba} onChangeDba={onChangeDba} />
        <this.InstructionsText />
      </>
    }
  }

  ConsentTextOverrideToggle = ({ onChange, onChangeDba, defaultConsentText, defaultConsentDba }) => {
    const { col } = this.props

    return <Col size='3'>
      <FormGroup>
        <Label>Override Consent Text</Label>
        <PureSwitch
          onChange={e => {
            if (e.target.checked) {
              onChange(defaultConsentText)
              onChangeDba(defaultConsentDba)
            } else {
              onChange(undefined)
              onChangeDba(undefined)
            }
          }}
          value={typeof col.consent_override_text === 'string'} />
      </FormGroup>
    </Col>
  }

  SaveConsentText = () => {
    const { col } = this.props
    const handleChangeField = this.handlePropChange(col)
    const handleChange = handleChangeField("save_tcpaconsent_text")

    if (!['tcpa_consent', 'bundled_consent'].includes(col.field)) {
      return null
    }

    return <Col size="3">
      <FormGroup>
        <Label>Save TCPA Consent Text</Label>
        <PureSwitch
          onChange={e => handleChange(e.target.checked)}
          value={col.save_tcpaconsent_text || false}
        />
      </FormGroup>
    </Col>
  }

  ButtonsConfig = ({ onChange }) => {
    const { col } = this.props
    if (col.field_component !== "buttons_grid" && col.field_component !== "buttons_grid_boolean") {
      return null
    }

    return <>
      <Col size="3">
        <FormGroup>
          <Label>Column buttons</Label>
          <Select
            clearable={false}
            options={COL_SIZES}
            name="gridCols"
            value={col.gridCols}
            required
            onChange={e => {
              onChange("gridCols")(e.value)
              onChange("colSize")(Math.floor(12 / e.value))
            }}
          />
        </FormGroup>
      </Col>
      <Col size="3">
        <FormGroup>
          <Label>Text align</Label>
          <Select
            clearable={false}
            options={[
              { value: "left", label: "Left" },
              { value: "center", label: "Center" },
              { value: "right", label: "Right" }
            ]}
            name="textDirection"
            value={col.textDirection}
            onChange={e => onChange("textDirection")(e.value)}
          />
        </FormGroup>
      </Col>
      <Col size="3">
        <FormGroup>
          <Label>
            Button Padding
            <InfoTip side='left' id="button-text-padding">
              Please insert a number with the unit of measure (px, em, rem, etc).
            </InfoTip>
          </Label>
          <Input
            name="textPadding"
            value={col.textPadding}
            onChange={e => onChange("textPadding")(e.target.value)}
          />
        </FormGroup>
      </Col>
      {col.field_component !== "buttons_grid_boolean" && <Col size="3">
        <FormGroup>
          <Label>Multiple Answers</Label>
          <PureSwitch
            value={col.multipleAnswers}
            onChange={e => onChange("multipleAnswers")(e.target.checked)}
          />
        </FormGroup>
      </Col>}
    </>
  }

  DefaultOptionComponent = ({ onChange, label, value }) => {
    const { col } = this.props
    const [enabled, setEnabled] = useState(!!value)

    const handleChangeCheckbox = useCallback(() => {
      onChange("")
      setEnabled(prevState => !prevState)
    }, [onChange, setEnabled])

    return (
      <Col size="4">
        <FormGroup>
          <Label>{label || ""} Default option</Label>
          <InputGroup>
            <InputGroupText addonType='prepend'>
              <Checkbox
                value={enabled}
                onChange={checked => handleChangeCheckbox(checked)}
                wrapClassName='m-0'
              />
            </InputGroupText>
            {!enabled ? <Input disabled value={value || ""} placeholder='disabled' className='italic' /> : <Input
              value={value || ""}
              onChange={e => onChange(e.target.value)}
              placeholder="Type the option label"
            />}
          </InputGroup>
        </FormGroup>
      </Col>
    )
  }

  DefaultOption = ({ onChangeField }) => {
    const { col } = this.props;

    if (!(col.field_component === "select" || col.field_component === "statelist" || col.field === "state" || col.field === "dob" || col.field_component === "date_triple_select")) return null;

    if (col.field === 'dob' || col.field_component === 'date_triple_select') {
      return (
        <>
          <this.DefaultOptionComponent onChange={onChangeField("defaultOptionMonth")} value={col.defaultOptionMonth} label="Month" />
          <this.DefaultOptionComponent onChange={onChangeField("defaultOptionDay")} value={col.defaultOptionDay} label="Day" />
          <this.DefaultOptionComponent onChange={onChangeField("defaultOptionYear")} value={col.defaultOptionYear} label="Year" />
        </>
      )
    }
    return (
      <this.DefaultOptionComponent onChange={onChangeField("defaultOption")} value={col.defaultOption} />
    );
  }

  SearchFilter = ({ onChange }) => {
    const { col: { custom_field, field_component, field_datasource, search_filter, field_type } } = this.props
    const { datasourceTemplates } = useFormStore()
    if (!custom_field || !!field_datasource || ['select'].includes(field_component) || (custom_field && field_type === 'date')) {
      return null
    }

    const loadTemplateOptions = datasourceTemplates.search_filter

    return <Col size="4">
      <FormGroup>
        <Label>Search Filter</Label>
        <TemplateGroup
          value={search_filter}
          onChange={onChange}
          loadOptions={loadTemplateOptions}
        />
      </FormGroup>
    </Col>
  }

  Component = ({ onChange }) => {
    const { col, selectedFields } = this.props
    const onChangeComponent = onChange("field_component")

    if (!col.custom_field && !['gender', 'title', 'tcpa_consent', 'registration_consent', 'recording_consent', 'bundled_consent', 'heading'].includes(col.field)) {
      return null
    }

    if (isEmpty(selectedFields)) {
      return null
    }

    const options = this.getComponents(col)

    const handleChange = e => {
      if (col.field_component === 'checkbox') {
        col.autohide = false
      }
      if (col.field_component === 'text') {
        col.defaultValue = false
      }

      if (e.value === 'use_reg_consent') {
        col.consent_override_text = undefined
        col.consent_override_dba = undefined
        col.field_required = undefined
        col.defaultValue = undefined
        if (col.controls?.validations) {
          col.controls.validations = null
        }
      }

      onChangeComponent(e.value)
    }

    useEffect(() => {
      if (options.length) {
        const selected = options.some(({ value }) => value === col.field_component)
        if (!selected) {
          handleChange({ value: options[0].value })
        }
      }
    }, [options.length])

    this.hasExtraProperties = this.hasExtraProperties || true

    return <>
      <Col size="4">
        <FormGroup>
          <Label>Component</Label>
          <Select clearable={false} name="field_component" value={col.field_component} required
            options={options}
            onChange={handleChange} />
        </FormGroup>
      </Col>
      <this.SearchFilter onChange={onChange("search_filter")} />
    </>
  }

  DisableAlert = ({ onChange }) => {
    const { col } = this.props

    if (['tcpa_consent', 'registration_consent', 'recording_consent', 'bundled_consent'].includes(col.field) && ['checkbox', 'checkbox_instructions_text'].includes(col.field_component)) {
      return <Col size="2">
        <FormGroup>
          <Label>Disable Alert</Label>
          <Switch
            value={col.disableAlert || false}
            onChange={e => onChange(e.target.checked)}
          />
        </FormGroup>
      </Col>
    }
    return null
  }

  DefaultValue = ({ onChange }) => {
    const { col } = this.props

    if (['tcpa_consent', 'registration_consent', 'recording_consent', 'bundled_consent'].includes(col.field) && ['checkbox', 'checkbox_instructions_text'].includes(col.field_component)) {
      this.hasExtraProperties = this.hasExtraProperties || true
      return <Col size="2">
        <FormGroup>
          <Label>Checked</Label>
          <Switch
            value={col.defaultValue || false}
            onChange={e => onChange(e.target.checked)}
          />
        </FormGroup>
      </Col>
    }
    return null
  }

  DefaultYear = ({ onChange }) => {
    const { col } = this.props

    if (['dob'].includes(col.field)) {
      this.hasExtraProperties = this.hasExtraProperties || true
      return <Col size="8">
        <FormGroup>
          <Label>Default Year</Label>
          <Input type='number' value={col.defaultYear || ''} onChange={e => onChange(e.target.value)} />
        </FormGroup>
      </Col>
    }
    return null
  }

  TwyneTrigger = ({ onChangeField }) => {
    const { col } = this.props

    if (!['twyne_registration'].includes(col.field)) {
      return null
    }

    this.hasExtraProperties = this.hasExtraProperties || true

    const options = fieldsUtil.twyneRegistrationTriggerTypes

    const handleChange = e => {
      onChangeField("twyne_reg_trigger_type").call(this, e.value)
    }

    useEffect(() => {
      if (!col.twyne_reg_trigger_type) {
        handleChange({ value: options[0].value })
      }
    }, [col.twyne_reg_trigger_type])

    return <>
      <Col size='12'>
        <FormGroup>
          <Label>Trigger</Label>
          <Select clearable={false} name="twyne_reg_trigger_type" value={col.twyne_reg_trigger_type} required
            options={options}
            onChange={handleChange} />
        </FormGroup>
      </Col>
      <this.TwyneTriggerType onChangeField={onChangeField} />
    </>
  }

  JumpURL = ({ onChange }) => {
    const { col } = this.props

    if (col.field === 'jumper') {
      this.hasExtraProperties = this.hasExtraProperties || true
      return <Col size="12">
        <FormGroup>
          <Label>Jump URL</Label>
          <Input value={col.jump_url || '/'} required
            onChange={e => onChange(e.target.value)} />
        </FormGroup>
      </Col>
    }
    return null
  }

  AutoSubmit = ({ onChange }) => {
    const { hasAutoSubmitBehavior } = this

    useEffect(() => {
      if (!hasAutoSubmitBehavior) onChange(false)
    }, [hasAutoSubmitBehavior])

    if (!hasAutoSubmitBehavior) {
      return null
    }

    const { col, target } = this.props

    const id = `${target}-auto-submit`

    return <Col size='3'>
      <FormGroup>
        <Label>
          Auto Submit Form
          <InfoTip side='top' contentProps={{ className: 'max-w-32' }}  id={id}>
            When this field value changes and all the validations were valid then the form going to be submitted.
          </InfoTip>
        </Label>
        <Switch
          value={col.auto_submit || false}
          onChange={e => onChange(e.target.checked)}
        />
      </FormGroup>
    </Col>
  }

  Control = ({ onChangeField }) => <>
    <this.TwyneOverrideToggle onChange={onChangeField("twy_override_config")} />
    <this.Component onChange={onChangeField} />
    <this.DefaultValue onChange={onChangeField("defaultValue")} />
    <this.DefaultYear onChange={onChangeField("defaultYear")} />
    <this.TwyneTrigger onChangeField={onChangeField} />
    <this.DefaultOption onChangeField={onChangeField} />
    <this.AutoSubmit onChange={onChangeField('auto_submit')} />
    <this.DisableAlert onChange={onChangeField("disableAlert")} />
  </>

  TwyneOverride = ({ onChangeField }) => {
    const { col } = this.props

    if (col.twy_override_config) {
      this.hasExtraProperties = this.hasExtraProperties || true
      return <Col size='12'>
        <Row>
          <Col size='6'>
            <FormGroup>
              <Label>URL</Label>
              <TwyneURL name="twyne-url" value={col.twy_url || ''} required onChange={twyurl => onChangeField('twy_url').call(this, twyurl)} />
            </FormGroup>
          </Col>
          <Col size='3'>
            <FormGroup>
              <Label>Publisher</Label>
              <InputForm name="pub-twyne" value={col.twy_pid || ''} required onChange={e => onChangeField("twy_pid").call(this, e.target.value)} />
            </FormGroup>
          </Col>
          <Col size='3'>
            <FormGroup>
              <Label>Source</Label>
              <InputForm name="source-twyne" value={col.twy_sid || ''} required onChange={e => onChangeField("twy_sid").call(this, e.target.value)} />
            </FormGroup>
          </Col>
        </Row>
      </Col>
    }
    return null
  }

  ConsentText = ({ onChange, defaultConsentDba, onChangeDba }) => {
    const { col } = this.props

    if (typeof col.consent_override_text !== 'string') {
      return null
    }

    return <Col size='12'>
      <FormGroup>
        <div className='d-flex justify-content-between'>
          <Label>Consent Text
            <ContinueNoConsent
              targetPaste='consent_override_text'
              id={`consent-hint-${col.field}`}
            />
          </Label>
          {['bundled_consent', 'tcpa_consent'].includes(col.field) && <>
            <TcpaConsentDbaProvider>
              <TcpaConsentDbaTriggerButton
                tcpaConsentDba={col.consent_override_dba || defaultConsentDba}
                tcpaConsentText={col.consent_override_text}
              />
              <TcpaConsentDbaModal
                onChange={onChangeDba}
                override
                overrideValues={col.consent_override_dba}
              />
            </TcpaConsentDbaProvider>
          </>}
        </div>
        <CodeEditor
          required
          height="10em"
          width="100%"
          name='consent_override_text'
          value={col.consent_override_text || ''}
          onChange={value => onChange(value)}
        />
      </FormGroup>
    </Col>
  }

  InstructionsText = () => {
    const { col } = this.props
    const handleChangeField = this.handlePropChange(col)
    const handleChange = handleChangeField("instructions_text")

    if (col.field_component !== 'checkbox_instructions_text') {
      return null
    }

    return <Col size="12">
      <FormGroup>
        <Label>Instructions Text</Label>
        <CodeEditor
          required
          height="10rem"
          width="100%"
          name="instructions_text"
          value={col.instructions_text || ""}
          onChange={value => handleChange(value)}
        />
      </FormGroup>
    </Col>
  }

  LeadidCampaing = ({ onChange }) => {
    const { col } = this.props
    const [leadidKey, setLeadidKey] = useState(null)
    const { siteSettings: { useSystemConfig } } = useGlobalStore()

    const leadid_key = useSystemConfig('jornaya.leadid_key')
    useEffect(() => {
      setLeadidKey(leadid_key || '')
    }, [leadid_key])

    if (leadidKey === null) {
      return null
    }

    if (col.field === 'recording_consent') {
      return <Col size='12'>
        <Row>
          <this.LeadidCampaingToggle onChange={onChange} />
          <this.LeadidCampaingKey onChange={onChange} defaultLeadidKey={leadidKey} />
        </Row>
      </Col>
    } else if (col.leadid_campaignid) {
      onChange(undefined)
    }
    return null
  }

  LeadidCampaingToggle = ({ onChange }) => {
    const { col } = this.props

    this.hasExtraProperties = this.hasExtraProperties || true
    return <Col size='2'>
      <FormGroup>
        <Label>Override Leadid Camping ID</Label>
        <PureSwitch
          onChange={e => e.target.checked ? onChange('') : onChange(undefined)}
          value={typeof col.leadid_campaignid === 'string'} />
        <em />
      </FormGroup>
    </Col>
  }

  LeadidCampaingKey = ({ onChange, defaultLeadidKey }) => {
    const { col } = this.props

    this.hasExtraProperties = this.hasExtraProperties || true

    return <Col size='10'>
      <FormGroup>
        <Label>Jornaya Leadid Key</Label>
        {typeof col.leadid_campaignid === 'string' ? <InputForm name='leadid_campaignid' value={col.leadid_campaignid} onChange={e => onChange(e.target.value)} /> : <Input value={defaultLeadidKey} disabled />
        }
      </FormGroup>
    </Col>
  }

  DisableDynamic = () => {
    const { col, target } = this.props

    if (!['html', 'label', 'heading'].includes(col.field)) {
      return null
    }

    const id = `${target}-html-dynamic-tip`

    const onChange = this.handlePropChange(col).call(null, "disabled_dynamic")

    return <Col size='4'>
      <FormGroup>
        <Label>
          Disable Dynamic Populate
          <InfoTip id={id}>
            If disabled then all the populate dynamic behaviors (prepopulate-with, populate-options and mergetags) are going to be disabled and this field is not going to be updated when the form changes.
          </InfoTip>
        </Label>
        <Switch
          value={col.disabled_dynamic || false}
          onChange={e => onChange(e.target.checked)}
        />
      </FormGroup>
    </Col>
  }

  HTML = ({ onChange }) => {
    const { col } = this.props

    if (col.field === 'html') {
      this.hasExtraProperties = this.hasExtraProperties || true
      return <Col size='12'>
        <FormGroup>
          <Label>HTML</Label>
          <CodeEditor name='html_field' value={col.html || ''}
            width="100%" height="30em" onChange={value => onChange(value)} onValidate={this.props.onHtmlEditorValidate} />
        </FormGroup>
      </Col>
    }
    return null
  }

  Label = ({ onChange }) => {
    const { col } = this.props

    if (col.field === 'jumper' || col.field === 'html' || col.field === 'timer_submit') {
      col.label = null
      return null
    }
    return <Col size="6">
      <FormGroup>
        <Label>Label</Label>
        <this.LabelInput value={col.label || ''} onChange={onChange} name={`label-${this.props.target}`} />
      </FormGroup>
    </Col>
  }

  LabelInput = ({ onChange, ...props }) => {
    const { col, customFields: fields } = this.props

    if (col.field !== 'label' && col.field !== 'heading') {
      return <InputForm {...props} onChange={event => onChange(event.currentTarget.value)} />
    }

    const [mergetags, setMergetags] = useState(fieldsUtil.mergetagsTemplates)
    const [customFields, setCustomFields] = useState([])

    useEffect(() => {
      if (fields.length) {
        const extraFields = []
        for (const field of fields) {
          if (!field.key.startsWith('cf') || field.private) {
            continue
          }

          extraFields.push([field.key, field.key])
          if (field.type === 'date') {
            extraFields.push(...getDateFormats(field.key).map(key => [key, key]))
          }
        }
        setMergetags(mergetags => [].concat(mergetags, extraFields))

        setCustomFields(extraFields)
      }
    }, [fields.length])

    const checkMergetag = useCallback(value => {
      if (!customFields.length) {
        return true
      }

      return !/[{}]/.test(value.replace(/{(\w+)}/g, ($1, $2) => mergetags.find(d => d && d[0] === $2) ? '' : $1))
    }, [mergetags])

    return <InputGroup>
      <InputForm
        {...props}
        onChange={event => onChange(event.currentTarget.value)}
        id={props.name}
        validations={{
          mergeTagValid: (_, value) => checkMergetag(value),
        }}
        validationErrors={{
          mergeTagValid: 'Merge Tags invalid',
        }}
      />
      <DropdownMergeTag target={props.name} mergetags={mergetags} onChange={onChange}>
        <Button color='accent' disabled={col.disabled_dynamic}>
          MergeTag
        <ChevronDown size={16} />
        </Button>
      </DropdownMergeTag>
      </InputGroup>
  }

  Class = ({ onChange }) => {
    const { col } = this.props

    return <Col size="6">
      <FormGroup>
        <Label>Class</Label>
        <Input name="class" value={col.class || ''} onChange={e => onChange(e.target.value)} />
      </FormGroup>
    </Col>
  }

  ColSize = ({ onChange }) => {
    const { col } = this.props
    const [value, setValue] = useState('')

    useEffect(() => {
      col.size && setValue(col.size)
    }, [col.size])

    return <Col size='4'>
      <FormGroup>
        <Label>Col Size</Label>
        <Select
          name='sd'
          clearable={false}
          value={value}
          options={this.colsSizeOpts}
          onChange={item => {
            setValue(item.value)
            onChange(item.value)
          }}
        />
      </FormGroup>
    </Col>
  }

  FieldProperties = () => {
    const { col } = this.props

    const handleChangeField = this.handlePropChange(col)

    return <>
      <this.Label onChange={handleChangeField('label')} />
      <this.Class onChange={handleChangeField('class')} />
      <this.ColSize onChange={handleChangeField('size')} />
    </>
  }

  ExtraProperties = () => {
    const { col } = this.props
    const handleChangeField = this.handlePropChange(col)

    if (this.hasExtraProperties(col)) {
      return <>
        <this.JumpURL onChange={handleChangeField("jump_url")} />
        <this.Control onChangeField={handleChangeField} />
        <this.TwyneOverride onChangeField={handleChangeField} />
        <this.TimerSubmit onChangeField={handleChangeField} />
        <this.Consent onChange={handleChangeField("consent_override_text")} onChangeDba={handleChangeField("consent_override_dba")} />
        <this.LeadidCampaing onChange={handleChangeField('leadid_campaignid')} />
        <this.HTML onChange={handleChangeField("html")} />
        <this.ButtonsConfig onChange={handleChangeField} />
      </>
    }

    return null
  }

  AlertFeedbacks = () => {
    const { col } = this.props

    if (col.field_component === 'use_reg_consent') {
      return <Col size='12'>
        <SimpleAlert color='warning'>
          Be careful. When select the component "Use registration consent" make sure the field <Badge color='warning' size='sm'>Registration Consent</Badge> is added to the same step and before the current field.
        </SimpleAlert>
      </Col>
    }
    if (col.field === 'timer_submit') {
      return <Col size='12'>
        <SimpleAlert color='warning'>
          When add the field "Timer Submit" the submit button is going to be hidden. After the step is submitted the button will be visible again.
        </SimpleAlert>
      </Col>
    }
    return null
  }

  PreventSkipStep = () => {
    const { col, target } = this.props

    if (!["submit", "label", "html", "previous_step_button", "heading"].includes(col.field)) {
      return null
    }

    const id = `${target}-prevent-skip`

    const onChange = this.handlePropChange(col).call(null, "prevent_skip")

    return <Col size='4'>
      <FormGroup>
        <Label>
          Prevent Skip Step
          <InfoTip id={id} side='left'>
            Enabled this toggle to force the current step to be shown even if there weren't visible PII field.
          </InfoTip>
        </Label>
        <Switch
          value={col.prevent_skip || false}
          onChange={e => onChange(e.target.checked)}
        />
      </FormGroup>
    </Col>
  }

  render() {
    const { children } = this.props
    return <>
      <this.FieldProperties />
      <this.ExtraProperties />
      {children}
      <this.DisableDynamic />
      <this.PreventSkipStep />
      <this.AlertFeedbacks />
    </>
  }
}

